import {
  CardHeader,
  CardsStack,
  DecisionCardSurface,
  MetricCell,
  MetricRow,
  MetricsGroup,
  OverlayRoot,
  ToggleButton,
} from "@Components/game/builder/styled-components";
import { DecisionCard, getDecisionMetrics } from "@Containers/game/builder/builder-decision-utils";
import useI18n from "@i18n";
import { Alert, CircularProgress, Snackbar, Stack, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";

interface AutoAssignToast {
  severity: "success" | "error";
  message: string;
}

interface BuilderDecisionOverlayProps {
  decisionCards: DecisionCard[];
  showDecisionCards: boolean;
  setShowDecisionCards: Dispatch<SetStateAction<boolean>>;
  onAutoAssignDecisionScores?: () => Promise<{ status: "success" | "error"; message: string } | undefined> | void;
  autoAssignLoading?: boolean;
}

const BuilderDecisionOverlay = ({
  decisionCards,
  showDecisionCards,
  setShowDecisionCards,
  onAutoAssignDecisionScores,
  autoAssignLoading = false,
}: BuilderDecisionOverlayProps) => {
  const { i18n } = useI18n();
  const [toast, setToast] = useState<AutoAssignToast | null>(null);

  const handleAutoAssignClick = async () => {
    const result = await onAutoAssignDecisionScores?.();
    if (!result) return;

    setToast({
      severity: result.status,
      message: result.message,
    });
  };

  return (
    <OverlayRoot>
      <Stack direction="row" spacing={1} sx={{ pointerEvents: "auto" }}>
        <ToggleButton
          active={showDecisionCards}
          color={showDecisionCards ? "primary" : "secondary"}
          size="small"
          onClick={() => setShowDecisionCards(prev => !prev)}
        >
          <Typography variant="textS600">
            {showDecisionCards ? i18n("builder.decision.toggle.hide") : i18n("builder.decision.toggle.show")}
          </Typography>
        </ToggleButton>
        <ToggleButton active={false} color="secondary" size="small" onClick={handleAutoAssignClick} disabled={autoAssignLoading}>
          <Stack direction="row" spacing={0.9} alignItems="center" justifyContent="center">
            {autoAssignLoading && <CircularProgress size={14} sx={{ color: "dsBase.v200" }} />}
            <Typography variant="textS600">{i18n("builder.decision.auto.timeline.score")}</Typography>
          </Stack>
        </ToggleButton>
      </Stack>

      {showDecisionCards && decisionCards.length > 0 && (
        <CardsStack spacing={1.25} onWheel={event => event.stopPropagation()}>
          {decisionCards.map(card => (
            <DecisionCardSurface key={`decision-card-${card.index}`} elevation={0} accentColor={card.accentColor}>
              <Stack spacing={1.2}>
                <CardHeader direction="row" spacing={1.5}>
                  <Typography
                    variant="textBase600"
                    sx={{
                      color: "dsBase.v200",
                      lineHeight: 1.2,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {i18n("builder.decision.option", { index: String(card.index + 1) })}
                  </Typography>
                  <Typography
                    variant="text3xl700"
                    sx={{
                      color: "dsBase.v200",
                      lineHeight: 1,
                      letterSpacing: "-0.03em",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {Math.round(card.option.displayScore ?? 0)}%
                  </Typography>
                </CardHeader>

                <MetricsGroup>
                  {getDecisionMetrics(card.option).map((metricRow, rowIndex) => (
                    <MetricRow key={`${card.index}-semantic-row-${rowIndex}`}>
                      {metricRow.map(metric => (
                        <MetricCell key={`${card.index}-${metric.labelKey}-label`}>
                          <Typography
                            variant="textXs600"
                            sx={{
                              color: "dsNeutral.v300",
                              lineHeight: 1.15,
                            }}
                          >
                            {i18n(metric.labelKey)}
                          </Typography>
                          <Typography
                            variant="text1xl600"
                            sx={{
                              color: "dsBase.v200",
                              lineHeight: 1.15,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {metric.value}
                          </Typography>
                        </MetricCell>
                      ))}
                    </MetricRow>
                  ))}
                </MetricsGroup>
              </Stack>
            </DecisionCardSurface>
          ))}
        </CardsStack>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4500} onClose={() => setToast(null)} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={toast?.severity ?? "success"} onClose={() => setToast(null)} sx={{ width: "100%", pointerEvents: "auto" }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </OverlayRoot>
  );
};

export default BuilderDecisionOverlay;
